import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, AlertTriangle } from "lucide-react";

/**
 * Caricamento del listino materiali: prima si vede cosa succederebbe, poi si
 * conferma. Con ottocento articoli in gioco, importare alla cieca e scoprire
 * dopo di aver sbagliato colonna sarebbe un guaio da ripulire a mano.
 */
export default function ImportListinoDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const scegli = async (f) => {
    if (!f) return;
    setFile(f); setAnteprima(null); setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/materials/import/preview", fd,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      setAnteprima(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Non riesco a leggere il file");
      setFile(null);
    } finally { setBusy(false); }
  };

  const importa = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/materials/import", fd,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inseriti} articoli importati`);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'importazione");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Importa il listino materiali</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Il foglio deve avere almeno la colonna <strong>Descrizione</strong>. Categoria,
            unità, prezzo, scorta minima e preferito vengono presi se ci sono. Se c'è anche
            una colonna <strong>Giacenza</strong>, quelle quantità entrano come inventario di
            partenza; altrimenti si parte da zero e le quantità arrivano con i carichi.
          </p>

          <input ref={ref} type="file" accept=".xlsx,.xlsm" hidden
                 onChange={(e) => scegli(e.target.files?.[0])} />
          <Button variant="outline" className="w-full h-12" onClick={() => ref.current?.click()}
                  disabled={busy} data-testid="scegli-listino">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {file ? file.name : "Scegli il file Excel"}
          </Button>

          {busy && !anteprima && <p className="text-sm text-slate-400">Lettura in corso…</p>}

          {anteprima && (
            <div className="space-y-3" data-testid="anteprima-listino">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Nuovi", anteprima.nuovi, "text-emerald-700"],
                  ["Già presenti", anteprima.gia_presenti, "text-slate-500"],
                  ["Doppi nel file", anteprima.doppi_nel_file, "text-amber-700"]].map(([l, v, c]) => (
                  <div key={l} className="border border-slate-200 rounded p-2">
                    <div className={`text-2xl font-bold ${c}`}>{v}</div>
                    <div className="text-[11px] text-slate-500">{l}</div>
                  </div>
                ))}
              </div>

              {anteprima.con_giacenza > 0 && (
                <p className="text-sm text-emerald-700">
                  {anteprima.con_giacenza} articoli entrano già con la loro giacenza:
                  il magazzino parte contato.
                </p>
              )}

              {anteprima.nuovi === 0 && (
                <p className="text-sm text-amber-700 flex items-start gap-1.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Non c'è niente di nuovo da importare: questi articoli sono già in archivio.
                </p>
              )}

              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">
                  Categorie che entrerebbero
                </div>
                <div className="flex flex-wrap gap-1">
                  {anteprima.categorie.map(c => (
                    <span key={c.categoria}
                          className="text-xs border border-slate-200 rounded px-2 py-0.5">
                      {c.categoria} <span className="text-slate-400">{c.articoli}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">
                  Prime righe
                </div>
                <div className="border border-slate-200 rounded max-h-40 overflow-y-auto">
                  {anteprima.esempi.map((e, i) => (
                    <div key={i} className="text-xs px-2 py-1 border-b border-slate-100 last:border-0">
                      <span className="font-medium">{e.name}</span>
                      <span className="text-slate-400"> · {e.category} · al {e.unit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-[11px] text-slate-500">
                Colonne riconosciute: {anteprima.colonne_riconosciute.join(", ")}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={importa} disabled={busy || !anteprima || anteprima.nuovi === 0}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="conferma-import-listino">
            <Upload className="w-4 h-4 mr-2" />
            {busy ? "Importazione…" : `Importa ${anteprima?.nuovi ?? 0} articoli`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
